import type { ReactNode } from 'react';

export type SectionTone = 'base' | 'raised' | 'soft' | 'grid' | 'hero';

/*
 * 区块底色只做两件事：白面与近白底交替。
 * 曾经用来铺满整块区块的 #CEE6EF / #AAD4E4 已退场 —— 颜色交给渐变承担。
 */
const TONES: Record<SectionTone, string> = {
  base: 'bg-surface-0',
  raised: 'bg-surface-1 border-y border-line',
  soft: 'bg-surface-0 bg-grad-soft',
  grid: 'bg-surface-0 bg-grid',
  hero: 'bg-surface-0 bg-grad-hero',
};

export function Section({
  children,
  id,
  tone = 'base',
  className,
  spacing = 'default',
}: {
  children: ReactNode;
  id?: string;
  tone?: SectionTone;
  className?: string;
  spacing?: 'default' | 'tight' | 'loose' | 'none';
}) {
  const padding =
    spacing === 'none'
      ? ''
      : spacing === 'tight'
        ? 'py-10 md:py-14'
        : spacing === 'loose'
          ? 'py-20 md:py-32'
          : 'py-16 md:py-24';

  return (
    <section id={id} className={[TONES[tone], padding, className].filter(Boolean).join(' ')}>
      <div className="container-page">{children}</div>
    </section>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  lead,
  align = 'left',
  as: Tag = 'h2',
  actions,
  className,
}: {
  eyebrow?: string;
  title: ReactNode;
  lead?: ReactNode;
  align?: 'left' | 'center';
  as?: 'h1' | 'h2';
  actions?: ReactNode;
  className?: string;
}) {
  const alignment = align === 'center' ? 'items-center text-center mx-auto max-w-3xl' : 'items-start max-w-3xl';

  return (
    <div className={['flex flex-col gap-3.5', alignment, className].filter(Boolean).join(' ')}>
      {eyebrow ? <span className="label-mono text-brand">{eyebrow}</span> : null}
      <Tag className="text-[1.625rem] leading-[1.2] font-semibold text-fg md:text-[2.5rem]">{title}</Tag>
      {lead ? <p className="text-[0.9375rem] leading-relaxed text-fg-muted md:text-base">{lead}</p> : null}
      {actions ? <div className="mt-1">{actions}</div> : null}
    </div>
  );
}

/** 页头区：面包屑 + 标题 + 一句话说明 + 右侧动作。所有内页共用同一套版式。 */
export function PageHero({
  eyebrow,
  title,
  lead,
  meta,
  actions,
  aside,
}: {
  eyebrow?: string;
  title: string;
  lead?: string;
  meta?: ReactNode;
  actions?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <Section tone="hero" spacing="tight" className="border-b border-line">
      <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-center">
        <div className="flex flex-col gap-4">
          {eyebrow ? <span className="label-mono text-brand">{eyebrow}</span> : null}
          <h1 className="max-w-3xl text-[1.875rem] leading-[1.15] font-semibold text-fg md:text-[3.25rem] md:leading-[1.12]">
            {title}
          </h1>
          {lead ? <p className="max-w-2xl text-base leading-relaxed text-fg-muted">{lead}</p> : null}
          {meta ? <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-fg-muted">{meta}</div> : null}
          {actions ? <div className="mt-1 flex flex-wrap items-center gap-3">{actions}</div> : null}
        </div>
        {aside ? <div className="lg:justify-self-end">{aside}</div> : null}
      </div>
    </Section>
  );
}
